import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import toast from 'react-hot-toast'
import { Event, EventStatus } from './types'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

/**
 * Hook to fetch events that the current user can invite others to
 * Returns events organized by the current user that are not archived
 * Also returns draft events separately to show them in the UI with disabled state
 */
export const useInviteableEventsForUser = () => {
  const fetchWithAuth = useFetchWithAuth()

  return useQuery<{
    response: { connection: { nodes: Event[]; draftNodes: Event[] } }
  }>({
    queryKey: ['inviteableEvents'],
    queryFn: async () => {
      // Fetch all user's created events (high limit to ensure we get everything for search)
      const response = await fetchWithAuth(
        `${API_URL}/events?include_created=true&include_invited=false&include_open=false&limit=1000`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      )

      const now = new Date()

      // Separate events into inviteable and draft
      const inviteableEvents: Event[] = []
      const draftEvents: Event[] = []

      // Debug: log total events received from API
      console.log(
        `[InviteableEvents] Received ${(response || []).length} events from API`
      )
      ;(response || []).forEach((event: Event) => {
        const status = event.status?.toUpperCase()

        // Exclude archived events from both lists
        if (event.archived) {
          console.log(
            `[InviteableEvents] Filtered out "${event.name}" (${event.id}) - archived`
          )
          return
        }

        // Exclude cancelled/denied events from both lists (case-insensitive)
        if (status === 'CANCELLED' || status === 'DENIED') {
          console.log(
            `[InviteableEvents] Filtered out "${event.name}" (${event.id}) - status: ${status}`
          )
          return
        }

        // Exclude expired events - check dates.dates for date ranges (startDate/endDate)
        // Note: dates.times contains time-of-day strings like "16:30", NOT full dates
        const dateRanges = event.dates?.dates
        if (dateRanges && Array.isArray(dateRanges) && dateRanges.length > 0) {
          const hasActiveDateRange = dateRanges.some((dateRange: any) => {
            // Check for endDate (new format) or end (old format)
            const endDateStr = dateRange.endDate || dateRange.end
            if (!endDateStr) return true // No end date means still active
            const endDate = new Date(endDateStr)
            return endDate >= now
          })
          if (!hasActiveDateRange) {
            console.log(
              `[InviteableEvents] Filtered out "${event.name}" (${event.id}) - all dates expired`
            )
            return
          }
        }

        // Separate DRAFT from inviteable (use the already-uppercased status)
        if (status === 'DRAFT') {
          draftEvents.push(event)
        } else {
          inviteableEvents.push(event)
        }
      })

      console.log(
        `[InviteableEvents] Result: ${inviteableEvents.length} inviteable, ${draftEvents.length} drafts`
      )

      return {
        response: {
          connection: {
            nodes: inviteableEvents,
            draftNodes: draftEvents,
          },
        },
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to invite a user (profile) to an event
 */
export const useInviteMatchToEvent = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: {
      variables: {
        invitation: {
          eventId: string
          profileId: string
        }
      }
    }) => {
      const { eventId, profileId } = variables.variables.invitation

      const response = await fetchWithAuth(
        `${API_URL}/events/${eventId}/invitations`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_ids: [profileId],
          }),
        }
      )
      return response
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['events'] })
      queryClient.invalidateQueries({ queryKey: ['userInvitations'] })
      toast.success('Invitation sent successfully')
    },
    onError: (error) => {
      console.error('Error sending invitation:', error)
      toast.error('Failed to send invitation')
    },
  })
}
